import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

const COMPANY_FIELD = "opportunity[contact_company_id]"
const EXCLUSIVE_TYPE_REASONS = new Set(["new_build", "tender", "subscription"])
// Never in a reload URL (a GET): the CSRF token would leak into URLs/logs.
const RELOAD_EXCLUDED = new Set(["authenticity_token", "_method"])
// Free text the rep types: kept out of the reload URL too (Puma caps the query
// string at 10 KB → a long pasted Details made the reload fail with "Content
// missing"). The reload replaces the whole fields frame, so the live values are
// carried into the re-rendered fields client-side instead (_carryFreeText).
const FREE_TEXT_FIELDS = ["opportunity[remarks]", "opportunity[name]"]

// Connects to data-controller="pipeline--opportunity-create"
//
// Glue for Pipeline::OpportunityCreateDrawerComponent — the Hotwire port of the
// React OpportunityDrawer a task drawer opened (CONEK-7690). Jobs:
//
//   * load      — when the drawer opens, point its `fields` frame at the
//                 server-rendered form with the host's default Site (React
//                 `defaultContactCompanyId`, read from `defaultCompanyFrom`).
//   * reload    — a structural change (Site, End User, Tender, New Build,
//                 source category, the contact toggle, add/remove participant)
//                 re-renders the form from the WHOLE current state plus a
//                 `reload_reason`; OpportunityDrawerForm applies React's rules.
//   * confirm   — Tender / New Build are one-way (CONEK-3956 / CONEK-3980):
//                 the first submit opens the matching confirmation instead; its
//                 accept submits for real (New Build wins when both are on).
//   * created   — 204 + X-Created-Opportunity-Id/-Name: select it in the host's
//                 Opportunity picker (`selectInto`) and close the drawer.
//   * linkSite  — CONEK-9171: link the IoT site picked in the IoT step to the
//                 customer (the same endpoint the React form uses), then
//                 re-render the step, with the refusal code when refused.
export default class extends Controller {
  static targets = [
    "fields",
    "form",
    "newBuild",
    "tender",
    "subscription",
    "newBuildConfirm",
    "tenderConfirm",
    "subscriptionConfirm",
  ]
  static values = {
    fieldsUrl: String,
    defaultCompanyFrom: String,
    selectInto: String,
    drawerId: String,
  }

  connect() {
    this._onDrawerOpen = this._onDrawerOpen.bind(this)
    this._onBeforeCache = this._onBeforeCache.bind(this)
    this._onVisibilityChange = this._onVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this._onVisibilityChange)
    // The drawer's opener lives in another drawer, so its open event bubbles to
    // the document, not necessarily through this element.
    document.addEventListener("drawer:open", this._onDrawerOpen)
    document.addEventListener("turbo:before-cache", this._onBeforeCache)
  }

  disconnect() {
    document.removeEventListener("drawer:open", this._onDrawerOpen)
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    document.removeEventListener("visibilitychange", this._onVisibilityChange)
    if (this._visibilityTimer) clearTimeout(this._visibilityTimer)
  }

  // CONEK-4743 parity: an address added in the customer's Locations tab (the
  // empty-state link opens it in a new tab) must surface when the rep comes back,
  // without a manual reload. 500 ms trailing debounce like React; armed only
  // while the drawer is open on a form that shows a Site Address.
  // CONEK-9171: the same for the IoT step, whose Create buttons open IoT in a
  // new tab — the IoT site, building or floorplan made there shows up here.
  _onVisibilityChange() {
    if (document.visibilityState !== "visible") return
    const drawer = document.getElementById(this.drawerIdValue)
    if (!drawer || drawer.classList.contains("hidden")) return
    const reason = this.element.querySelector(
      "[data-testid='opportunity-facilities']",
    )
      ? "facilities_refresh"
      : this.element.querySelector("[data-testid='site_address_id-input']")
        ? "site_address_refresh"
        : null
    if (!reason) return
    if (this._visibilityTimer) clearTimeout(this._visibilityTimer)
    this._visibilityTimer = setTimeout(() => this._reloadWith(reason, {}), 500)
  }

  async linkSite(event) {
    event.preventDefault()
    const { url, company } = event.params || {}
    const siteId = new FormData(this.formTarget).get("ui[facilities_site_link]")
    // A double click must not post twice: the second would be refused as
    // already linked and reload over the first one's success.
    if (!url || !company || !siteId || this._linking) return
    this._linking = true

    let code = null
    try {
      const response = await post(url, {
        body: { contact_company_id: company, facilities_site_id: siteId },
        responseKind: "json",
      })
      if (!response.ok) code = (await response.json)?.code || "unknown"
    } catch {
      code = "unknown"
    } finally {
      this._linking = false
    }
    this._reloadWith(
      "facilities_refresh",
      code ? { facilities_link_error: code } : {},
    )
  }

  _onDrawerOpen(event) {
    if (event.target?.id !== this.drawerIdValue) return
    this.load()
  }

  // A cached page must never restore a half-filled form (CONEK-6282): drop the
  // body; the next open fetches a fresh one.
  _onBeforeCache() {
    if (!this.hasFieldsTarget) return
    this.fieldsTarget.removeAttribute("src")
    this.fieldsTarget.replaceChildren()
  }

  load() {
    if (!this.hasFieldsTarget || !this.fieldsUrlValue) return
    const url = new URL(this.fieldsUrlValue, window.location.origin)
    const company = this._defaultCompany()
    if (company) url.searchParams.set("contact_company_id", company)
    this.confirmed = false
    this._dropFreeTextCarry()
    this._setSrc(url, true)
  }

  // Stimulus action params: reason (+ index for a participant row).
  reload(event) {
    const { reason, index } = event.params || {}
    if (!reason) return
    if (event.type === "click") event.preventDefault()
    if (reason === "company") {
      const field = event.target
      if (!field || field.name !== COMPANY_FIELD) return
      const previous = this.companyId ?? ""
      if (previous === field.value) return
      this.companyId = field.value
      this._reloadWith(reason, { previous_company_id: previous })
      return
    }
    if (EXCLUSIVE_TYPE_REASONS.has(reason))
      this._lockExclusiveTypes(event.target)
    this._reloadWith(
      reason,
      index === undefined ? {} : { participant_index: index },
    )
  }

  confirm(event) {
    if (this.confirmed) return
    const modal =
      this.hasNewBuildTarget && this.newBuildTarget.checked
        ? this.newBuildConfirmTarget
        : this.hasTenderTarget && this.tenderTarget.checked
          ? this.tenderConfirmTarget
          : this.hasSubscriptionTarget && this.subscriptionTarget.checked
            ? this.subscriptionConfirmTarget
            : null
    if (!modal) return
    event.preventDefault()
    event.stopImmediatePropagation()
    this._modal(modal)?.open()
  }

  acceptConfirm() {
    this._closeModals()
    this.confirmed = true
    this.formTarget.requestSubmit()
  }

  cancelConfirm() {
    this._closeModals()
  }

  created(event) {
    this.confirmed = false
    if (event.detail?.success !== true) return
    const headers = event.detail.fetchResponse?.response?.headers
    const id = headers?.get("X-Created-Opportunity-Id")
    if (!id) return
    const name = decodeURIComponent(
      headers.get("X-Created-Opportunity-Name") || "",
    )

    const input = this.selectIntoValue
      ? document.querySelector(this.selectIntoValue)
      : null
    const selectorEl = input?.closest(
      "[data-controller~='ui--entity-selector']",
    )
    const selector =
      selectorEl &&
      this.application.getControllerForElementAndIdentifier(
        selectorEl,
        "ui--entity-selector",
      )
    if (selector) selector._commitSelection(id, name || id)

    const drawer = document.getElementById(this.drawerIdValue)
    const drawerController =
      drawer &&
      this.application.getControllerForElementAndIdentifier(
        drawer,
        "ui--drawer",
      )
    drawerController?.close?.()
  }

  fieldsTargetConnected() {
    // Track the Site the rendered form shows, so a reload can say what changed.
    const field = this.element.querySelector(`[name="${COMPANY_FIELD}"]`)
    this.companyId = field ? field.value : ""
  }

  _reloadWith(reason, extra) {
    const url = new URL(this.fieldsUrlValue, window.location.origin)
    for (const [key, value] of new FormData(this.formTarget).entries()) {
      if (
        typeof value === "string" &&
        !RELOAD_EXCLUDED.has(key) &&
        !FREE_TEXT_FIELDS.includes(key)
      )
        url.searchParams.append(key, value)
    }
    url.searchParams.set("reload_reason", reason)
    Object.entries(extra).forEach(([key, value]) =>
      url.searchParams.set(key, String(value)),
    )
    this._carryFreeText()
    this._setSrc(url, false)
  }

  // Copy what the rep typed into the incoming fields just before Turbo swaps
  // them in — read from the CURRENT fields at render time, so a keystroke made
  // while the reload was in flight is kept too. Written as the fields' default
  // content (as a server echo would), so no input/change fires: nothing is
  // marked dirty, and pipeline--booking-ai connects to an already-filled field.
  _carryFreeText() {
    const frame = this.fieldsTarget
    this._dropFreeTextCarry()
    this._freeTextCarry = (event) => {
      this._freeTextCarry = null
      const incoming = event.detail?.newFrame
      if (!incoming) return
      FREE_TEXT_FIELDS.forEach((name) => {
        const selector = `[name="${name}"]`
        const current = frame.querySelector(selector)
        const next = incoming.querySelector(selector)
        if (!current || !next) return
        if (next.tagName === "TEXTAREA") next.textContent = current.value
        else next.setAttribute("value", current.value)
        next.value = current.value
      })
    }
    frame.addEventListener("turbo:before-frame-render", this._freeTextCarry, {
      once: true,
    })
  }

  // A fresh open (load) must never inherit a previous form's text.
  _dropFreeTextCarry() {
    if (!this._freeTextCarry || !this.hasFieldsTarget) return
    this.fieldsTarget.removeEventListener(
      "turbo:before-frame-render",
      this._freeTextCarry,
    )
    this._freeTextCarry = null
  }

  _setSrc(url, forceReload) {
    const frame = this.fieldsTarget
    const src = url.pathname + url.search
    if (
      forceReload &&
      frame.getAttribute("src") === src &&
      typeof frame.reload === "function"
    ) {
      frame.reload()
    } else {
      frame.setAttribute("src", src)
    }
  }

  _defaultCompany() {
    if (!this.defaultCompanyFromValue) return ""
    const input = document.querySelector(this.defaultCompanyFromValue)
    return input ? input.value : ""
  }

  _lockExclusiveTypes(changed) {
    if (!this.hasSubscriptionTarget) return
    const others = [
      this.hasNewBuildTarget && this.newBuildTarget,
      this.hasTenderTarget && this.tenderTarget,
    ].filter(Boolean)
    if (changed === this.subscriptionTarget && changed.checked) {
      others.forEach((field) => {
        field.checked = false
      })
    } else if (changed?.checked) {
      this.subscriptionTarget.checked = false
    }
    this.subscriptionTarget.disabled = others.some((field) => field.checked)
    others.forEach((field) => {
      field.disabled = this.subscriptionTarget.checked
    })
  }

  _modal(element) {
    return this.application.getControllerForElementAndIdentifier(
      element,
      "ui--modal",
    )
  }

  _closeModals() {
    ;[
      this.hasNewBuildConfirmTarget && this.newBuildConfirmTarget,
      this.hasTenderConfirmTarget && this.tenderConfirmTarget,
      this.hasSubscriptionConfirmTarget && this.subscriptionConfirmTarget,
    ]
      .filter(Boolean)
      .forEach((element) => this._modal(element)?.close?.())
  }
}
